import type { ReactNode } from 'react'
import { supabase } from './supabase'
import { viewingAs, viewAsDismissed, rememberViewAsDismissed } from './viewAsState'

/**
 * The notice strip at the top of the hub.
 *
 * ── TWO SOURCES, AND THE SPLIT IS THE POINT ─────────────────────────────────
 *
 * DATABASE notices (057) are written by comms: they carry an audience, an end
 * date, a locale, and a dismissal recorded per person. That is SHL-5, and it is
 * what FED-27 was filed about.
 *
 * CODE notices (lib/hubNotices.tsx) are build-state warnings — "some of this is
 * invented" — and they stay in code deliberately. They describe the BUILD, not
 * the company: a warning that the countdown is fake has no audience, no end
 * date, and no business being editable by Comms. It should disappear when the
 * last invented number does, which is a commit, not a database row.
 *
 * Do not migrate the placeholder notice into the database to "finish the job".
 *
 * ── DISMISSAL LIVES IN TWO PLACES, FOR TWO DIFFERENT REASONS ────────────────
 *
 * A database notice records a dismissal against the PERSON (David, 12 Sep), so
 * it follows somebody from the truck to the office and comms can count how many
 * of ~900 have put it away. `active_notices()` filters those out server-side.
 *
 * A code notice has no row to point at, so its dismissal stays in localStorage
 * — per browser, versioned, and explained below. That is not an inconsistency
 * to tidy up: one is a message to the company and the other is a note to the
 * five of us.
 *
 * ── THE VERSION RULE, WHICH KEEPS OPS-13 ALIVE ──────────────────────────────
 *
 * PlaceholderNotice was deliberately undismissable, because a prototype was
 * once reviewed as the build for three days. Making it dismissible weakens
 * that, and what holds it up is that a dismissal is stored AGAINST A VERSION:
 * bump a notice's `version` when you edit its body and it returns for everyone
 * who waved the old one away. Edit the text without bumping and it reaches
 * nobody.
 *
 * Every read and write is wrapped: a private window, cleared site data, or a
 * browser set to block storage all make localStorage throw rather than return
 * empty, and a notice strip is not worth a blank hub.
 */

const STORE_KEY = 'oneconetec.notices.dismissed.v1'

export type NoticeTone =
  /** Something on screen is invented. The OPS-13 guard. Red. Code-only. */
  | 'placeholder'
  /** Company news worth surfacing above the feed. Brand blue. */
  | 'campaign'

/** A notice the app ships in its bundle. See the header for why these exist. */
export type Notice = {
  id: string
  /** Bump when the BODY changes, or the edit reaches nobody. */
  version: number
  tone: NoticeTone
  tag: string
  body: ReactNode
  /**
   * A deliberate call to action. Markup written here, never a URL parsed out of
   * somebody's prose — nothing in this app linkifies free text, and a feed of
   * ~900 people's posts is not where to start. Internal hashes only.
   */
  action?: { label: string; href: string }
  dismissible: boolean
}

/**
 * A notice comms wrote (057). Shaped to render through the same component.
 *
 * `endsAt` is not rendered to a reader: "this message self-destructs in 3 days"
 * is noise to somebody who just wants to read it. It is on the comms board,
 * where the person who set it is the person who needs it.
 */
export type DbNotice = {
  contentId: string
  tag: string | null
  body: string
  ctaLabel: string | null
  ctaHref: string | null
  isDismissible: boolean
  placement: NoticePlacement
  ctaKeys: WelcomeCardKey[]
  variantLocale: string
  isFallback: boolean
  endsAt: string
}

/**
 * WHAT KIND OF INTERRUPTION A NOTICE IS — 091.
 *
 * `banner` is the strip above the hub, which is everything 057 built. `modal`
 * stops you at sign-in and is reopenable from the identity menu. It is a column
 * on `content_campaign_banner` rather than a new `promotion.slot` value: the
 * slot says where in the editorial layout published content sits and is shared
 * across content types, and rule 19 refuses a new enum member that changes what
 * the existing ones mean.
 */
export type NoticePlacement = 'banner' | 'modal'

/**
 * THE MODAL'S SECTION CARDS, AND THIS LIST IS HALF OF A CONTRACT.
 *
 * The other half is the `banner_cta_keys_known` CHECK, 091's, widened by 180
 * for the tour; the suite reads it from the NEWEST migration that defines it. The database
 * stores KEYS, chosen by comms; the wording and the destination live here,
 * because two of the four do not navigate at all — they open the Ask One
 * Ask One panel and so is not a URL (S2 moved "Tell us something" to #/help).
 *
 * That is also why this is safer than `cta_href`'s "any internal hash": a
 * notice is the one place in this app where a link goes in front of ~900 people
 * chosen by somebody other than the reader, and a closed set cannot be pointed
 * at a screen that does not exist.
 *
 * THE ORDER HERE IS THE ORDER ON SCREEN. The column is a SET: the renderer
 * walks this list and filters by membership, so neither the order the keys were
 * stored in nor a duplicated key can change what appears.
 *
 * The web suite asserts this list and the CHECK match IN BOTH DIRECTIONS — a
 * key in the database the app cannot render is a card that silently vanishes,
 * and a key here the CHECK refuses is a `create_notice` that fails at 3am.
 * 085's lane-keys posture, for 085's reason.
 */
export type WelcomeCardKey = 'video' | 'ask_one' | 'recognition' | 'tell_us' | 'tour'

export type WelcomeCard = {
  key: WelcomeCardKey
  title: string
  body: string
  /** What the button says. A verb, because it is an act and not a place. */
  action: string
} & (
  | { kind: 'navigate'; route: string }
  /** Opens the Ask One panel. S2 (31 Oct) removed the intent a card could
   *  declare: Ask One no longer routes, so a card about reaching a person
   *  NAVIGATES to #/help instead ("Tell us something"). */
  | { kind: 'drawer' }
  /** 180 (David, 12 Oct: "I like the tour more than the 4 links"). Starts the
   *  hub tour (SHL-34). Not a card in the grid: the welcome's main way out. */
  | { kind: 'tour' }
)

export const WELCOME_CARDS: readonly WelcomeCard[] = [
  {
    key: 'video',
    kind: 'navigate',
    route: '#/',
    title: 'Watch the announcement',
    body: 'Why the name is changing, and what it means for the work.',
    action: 'Watch it',
  },
  {
    key: 'ask_one',
    kind: 'drawer',
    title: 'Ask One a question',
    body: 'It answers from what has actually been published. If you need a person, Help is one tap away.',
    action: 'Ask something',
  },
  {
    key: 'recognition',
    kind: 'navigate',
    route: '#/stars',
    title: 'See who has been recognized',
    body: 'You have 1,000 Stars to give away each month. They reset, so use them.',
    action: 'Open Star One',
  },
  {
    key: 'tell_us',
    // S2 (31 Oct): opened the drawer with the platform intent declared; Ask
    // One no longer routes, so it opens Help, where Ticket and Feedback are.
    kind: 'navigate',
    route: '#/help',
    title: 'Tell us something',
    body: 'Found a bug, or thought of something the portal should do? It goes to the people building it.',
    action: 'Tell us',
  },
  {
    key: 'tour',
    kind: 'tour',
    title: 'Take the tour',
    body: 'A short walk round the hub, pointing at each part of it.',
    action: 'Take the tour',
  },
]

/** The cards this notice offers, in the app's canonical order. */
export function cardsFor(keys: readonly string[]): WelcomeCard[] {
  return WELCOME_CARDS.filter((c) => keys.includes(c.key))
}

/**
 * WHAT STATE A NOTICE IS IN — 093, AND THE SERVER DECIDES IT.
 *
 * This replaces `isLive`, which was the four states collapsed into two. The
 * board rendered `isLive ? 'Live' : revokedAt ? 'Taken down' : 'Expired'` and
 * printed "ended" with the end date for anything not live — so the welcome
 * notice scheduled for 13 October read "ended 13 Oct 2026". Not a wrong pill: a
 * false statement about a date the reader can check, which is standing rule 4's
 * worst shape.
 *
 * THE RULE IS NOT DUPLICATED HERE, AND THAT IS DELIBERATE. `app.notice_status`
 * is the one definition, `active_notices` FILTERS with it and
 * `notices_for_comms` LABELS with it. A client-side comparison of `startsAt`
 * against `Date.now()` would answer with the READER'S clock while the hub
 * answers with the DATABASE'S — so the two would disagree at the boundary,
 * which is the only moment anybody is looking.
 *
 * `startsAt` and `endsAt` come back as well, but as VALUES TO PRINT. Deriving
 * the state from them is the thing this type exists to prevent.
 */
export type NoticeStatus = 'pending' | 'live' | 'ended' | 'taken_down'

const NOTICE_STATUSES: readonly string[] = ['pending', 'live', 'ended', 'taken_down']

/** The pill for each state. One home, so a fifth reading cannot be invented. */
export const NOTICE_STATUS: Record<NoticeStatus, { label: string; pill: string }> = {
  // Amber is "unfinished" everywhere else in the app, and a notice waiting for
  // its day is exactly that — written, approved, not yet said.
  pending: { label: 'Pending', pill: 'pill pill--draft' },
  live: { label: 'Live', pill: 'pill pill--ok' },
  ended: { label: 'Ended', pill: 'pill pill--stale' },
  taken_down: { label: 'Taken down', pill: 'pill pill--stale' },
}

/**
 * THE ONE DATE EACH STATE IS ABOUT, AND THE WORD THAT GOES WITH IT.
 *
 * Every state points at a different date, which is why one line with a ternary
 * in it was never going to work: a pending notice is about its START, a live
 * one about its end, and a retired one about the day somebody pulled it — a
 * date the board did not show at all before 093.
 *
 * Returns the ISO string rather than a formatted date so the caller localises
 * it and the suite can assert on it without a locale.
 */
export function noticeWhen(n: {
  status: NoticeStatus
  startsAt: string
  endsAt: string
  revokedAt: string | null
}): { verb: string; iso: string } {
  switch (n.status) {
    case 'pending':
      return { verb: 'starts', iso: n.startsAt }
    case 'live':
      return { verb: 'until', iso: n.endsAt }
    case 'ended':
      return { verb: 'ended', iso: n.endsAt }
    case 'taken_down':
      // `revokedAt` is non-null exactly when the status is this, server side.
      // The fallback is a type narrowing, not a case that happens.
      return { verb: 'taken down', iso: n.revokedAt ?? n.endsAt }
  }
}

/** One notice on the comms board, with what a reader never sees. */
export type CommsNotice = DbNotice & {
  audience: string[]
  revokedAt: string | null
  status: NoticeStatus
  /** When it appears. A value to print — see `NoticeStatus` for why not a rule. */
  startsAt: string
  publishedAt: string
  authorName: string | null
  /**
   * How many people have put it away. A COUNT and never a list — the database
   * will not tell us who, deliberately (057, and 018's posture for reactions).
   * A roster of who has not yet read the safety notice is a management tool
   * nobody asked for.
   */
  dismissedCount: number
  /**
   * Which wording this is. 059, with 091's correction to what it CAUSES.
   *
   * Bumped by a TRIGGER on any edit to the words, the link or whether it can be
   * dismissed — never by a line in edit_notice(), because a counter a function
   * has to remember to increment is a counter the second write path will not.
   * That has not changed and must not: it is what makes "the wording moved"
   * un-forgeable.
   *
   * WHAT CHANGED IS THAT IT NO LONGER RE-SHOWS THE NOTICE BY ITSELF. Until 091
   * a dismissal below this number stopped suppressing, so every edit
   * re-interrupted everybody — David's 12 Sep ruling, taken about a BANNER
   * STRIP. On 18 September, looking at a modal, he reversed the consequence: a
   * typo fix must not re-show a modal to ~930 people. See `reshowAtRevision`.
   *
   * It is not an edit COUNT: one edit that moves both a variant field and a
   * banner field bumps it twice, because there is a trigger on each table.
   * Measured, harmless, and OPS-49.
   *
   * `dismissedCount` is the count for THIS revision, not for all time.
   */
  revision: number
  /**
   * The last wording the company DECIDED to interrupt people for. 091.
   *
   * A dismissal at or above this number suppresses the notice. It moves only
   * when somebody presses "Show it again" — `reraiseNotice` — so the gap
   * between this and `revision` is exactly "edits nobody has been re-asked
   * about". When they are equal there is nothing to re-raise, and the server
   * refuses rather than reporting a success that changed nothing.
   */
  reshowAtRevision: number
}

/**
 * The notice strip. Banners only, which is 091's DEFAULT and therefore exactly
 * what this call did before the column existed — a modal must never render in
 * the strip as a wall of text.
 */
export async function loadActiveNotices(): Promise<DbNotice[]> {
  const { data, error } = await supabase.rpc('active_notices', { p_placement: 'banner' })
  if (error) throw error
  return withoutViewAsDismissed((data ?? []).map(shape))
}

/** 176. While viewing as somebody, what was put away in this session stays
 *  away (lib/viewAsState.ts). Otherwise the list as the server sent it. */
function withoutViewAsDismissed(rows: DbNotice[]): DbNotice[] {
  if (!viewingAs()) return rows
  const gone = viewAsDismissed()
  return rows.filter((n) => !gone.has(n.contentId))
}

/**
 * The welcome modal, or nothing.
 *
 * `includeDismissed` is the identity menu's "Show me the welcome again" — a
 * REQUEST and not a permission (rule 20). The function is INVOKER, RLS decides
 * which notices exist at all, and all this relaxes is the suppression of THIS
 * READER'S OWN dismissal. A retired or out-of-window notice stays invisible
 * however it is asked for; the SQL suite asserts that, because a reopen path
 * with looser rules than the first read is a second answer to who may see what.
 *
 * ONE at most. Two modals at sign-in is two of the thing the drawer refused to
 * be, and `active_notices` already orders by rank then start date — so the
 * newest applicable welcome wins and the rest wait their turn rather than
 * stacking. On 13 October the manager notice has ended and the employee one has
 * started, so there is exactly one either way; this is what happens if a third
 * is ever added with an overlapping window.
 */
export async function loadWelcomeModal(
  includeDismissed = false,
): Promise<DbNotice | null> {
  const { data, error } = await supabase.rpc('active_notices', {
    p_placement: 'modal',
    p_include_dismissed: includeDismissed,
  })
  if (error) throw error
  const all = (data ?? []).map(shape)
  // 176: "show it again" still shows it; only the first read honours a close
  // made while viewing as somebody.
  const rows = includeDismissed ? all : withoutViewAsDismissed(all)
  return rows.length > 0 ? rows[0] : null
}

export async function loadCommsNotices(): Promise<CommsNotice[]> {
  const { data, error } = await supabase.rpc('notices_for_comms', {})
  if (error) throw error
  return (data ?? []).map((r: Record<string, never>) => ({
    ...shape(r),
    audience: r.audience ?? [],
    revokedAt: r.revoked_at ?? null,
    // A NARROWING, not a compatibility shim. Unlike `placement`, which defaults
    // because a row can predate its column, `status` is COMPUTED per read — so
    // there is no old row to be tolerant of and the server's CASE is
    // exhaustive. `ended` is the fallback only because it is the state that
    // offers no actions.
    status: NOTICE_STATUSES.includes(r.status) ? (r.status as NoticeStatus) : 'ended',
    startsAt: r.starts_at,
    publishedAt: r.published_at,
    authorName: r.author_name ?? null,
    dismissedCount: r.dismissed_count ?? 0,
    revision: r.revision ?? 1,
    reshowAtRevision: r.reshow_at_revision ?? 1,
  }))
}

function shape(r: Record<string, never>): DbNotice {
  return {
    contentId: r.content_id,
    tag: r.tag ?? null,
    body: r.body ?? '',
    ctaLabel: r.cta_label ?? null,
    ctaHref: r.cta_href ?? null,
    isDismissible: r.is_dismissible !== false,
    // Defaulted rather than asserted: a row from before 091 has no placement,
    // and a banner is the safe reading of a missing one — it renders in a strip
    // instead of interrupting somebody.
    placement: r.placement === 'modal' ? 'modal' : 'banner',
    ctaKeys: Array.isArray(r.cta_keys) ? (r.cta_keys as WelcomeCardKey[]) : [],
    variantLocale: r.variant_locale,
    isFallback: r.is_fallback === true,
    endsAt: r.ends_at,
  }
}

export type NewNotice = {
  body: string
  tag: string
  /**
   * When it appears. NULL means now, which is what every notice did before 093
   * and is still the ordinary case — so the field is blank by default and the
   * form says so.
   *
   * A day arrives as MIDNIGHT in the reader's own zone, which means "starts
   * today" is an instant several hours old. The server allows that on purpose:
   * refusing a past start would be a form that refuses its own default.
   */
  startsAt: string | null
  endsAt: string | null
  dismissible: boolean
  ctaLabel: string | null
  ctaHref: string | null
  audience: string[]
  /**
   * 091, and SET AT CREATION ONLY — `editNotice` deliberately cannot change
   * either of these. They say what the notice IS, which is rule 9's stable-key
   * clause, and 057's reason for freezing the audience applies unchanged:
   * turning a banner into a modal after four hundred people have dismissed it
   * re-shapes something that has already gone out. That is a take-down and a
   * new notice.
   */
  placement: NoticePlacement
  ctaKeys: WelcomeCardKey[]
}

export async function createNotice(n: NewNotice): Promise<string> {
  const { data, error } = await supabase.rpc('create_notice', {
    p_body: n.body,
    p_tag: n.tag || null,
    p_starts_at: n.startsAt,
    p_ends_at: n.endsAt,
    p_dismissible: n.dismissible,
    p_cta_label: n.ctaLabel,
    p_cta_href: n.ctaHref,
    p_audience: n.audience,
    // Omitted: p_locale defaults server-side to the author's own person.locale,
    // which has to be recorded as the SOURCE locale or resolve_variant serves
    // Spanish text to everyone as though it were the English original.
    p_placement: n.placement,
    p_cta_keys: n.ctaKeys,
    // BY NAME, every argument, and 093 is why this matters more than it did.
    // 091 APPENDED two parameters, which a positional caller survives. 093 put
    // `p_starts_at` in the MIDDLE — a window is a pair and reads as one — so
    // every argument after it shifted, and a positional caller would have
    // bound the end date to the start. 071 is the migration that learned it.
  })
  if (error) throw error
  return data as string
}

/**
 * PUT A NOTICE BACK IN FRONT OF EVERYBODY WHO DISMISSED AN OLDER WORDING.
 *
 * David, 18 Sep, superseding his own 12 Sep ruling. Editing a notice still
 * bumps its `revision` — by trigger, un-forgeable, 059's whole argument intact
 * — but that no longer re-shows it. A typo fix must not re-interrupt ~930
 * people, so re-showing is this: a separate act with its own name, its own
 * permission check and its own event row.
 *
 * Returns the revision people will now be asked about. REFUSES if the notice is
 * already showing at its current wording, rather than reporting a success that
 * changed nothing (rule 12).
 */
export async function reraiseNotice(contentId: string): Promise<number> {
  const { data, error } = await supabase.rpc('reraise_notice', { p_content_id: contentId })
  if (error) throw error
  return (data as number) ?? 0
}

/**
 * Change what a live notice says.
 *
 * ── A REPLACE, WITH NO DEFAULTS, AND THE CALLER SENDS EVERY FIELD ───────────
 *
 * 049's rule for set_employment, and the same trap: with defaults, "clear the
 * button" and "leave the button alone" become the same call and the silent
 * outcome is data loss. The editor shows every field, so it sends every field.
 *
 * That is why 093's `startsAt` is REQUIRED rather than optional, even though a
 * started notice may not move it: "leave it alone" in a replace is the row's
 * own value sent back, not an absent field.
 *
 * ── IT DOES NOT PUT THE NOTICE BACK UP. "SHOW IT AGAIN" DOES ────────────────
 *
 * THIS PARAGRAPH SAID THE OPPOSITE UNTIL 20 SEPTEMBER and was two days stale —
 * it still described David's 12 Sep ruling, which he reversed on the 18th when
 * 091 made a notice that can be a MODAL. `NoticeManager`'s on-screen warning
 * was corrected then and this was missed, which is the same fault one layer
 * down: a comment describing a control that does something else.
 *
 * Editing still bumps `revision`, by trigger, un-forgeable — that half is
 * untouched and is what makes "the wording moved" a fact. What it no longer
 * does is re-interrupt anybody. A dismissal suppresses until
 * `reshowAtRevision` moves, and only `reraiseNotice` moves it.
 *
 * ── BOTH ENDS OF THE WINDOW, OR NEITHER ─────────────────────────────────────
 *
 * `promotion` carries `ends_at > starts_at` AND `ends_at <= starts_at + 90
 * days`, so the two dates cannot be moved one at a time — `membership_
 * exception`'s shape. The server writes both in one statement; this function
 * has to send both for that to be possible.
 *
 * `p_locale` is left at its server default of en-CA. Editing a translation
 * means naming its locale, and nothing in the UI offers that yet — the function
 * RAISES on a locale with no variant rather than silently writing nothing,
 * which is 028c's lesson, so a future translations screen fails loudly if it
 * passes something wrong.
 */
/**
 * WHAT AN EDIT MAY CHANGE, WRITTEN OUT RATHER THAN SUBTRACTED FROM `NewNotice`.
 *
 * It used to be `Omit<NewNotice, 'audience'>`, and when 091 added `placement`
 * and `ctaKeys` the compiler correctly refused — an Omit silently inherits
 * every new field, so the next one added to creation would have arrived here as
 * an editable field nobody decided to make editable.
 *
 * Three things are deliberately absent, for the same reason in three costumes:
 * `audience` (057 — re-aiming a notice that has gone out removes it from people
 * who read it and pushes it at people who never had it, with no record of
 * either), and `placement` + `ctaKeys` (091 — they say what the notice IS, and
 * rule 9's stable-key clause). All three are a take-down and a new notice.
 */
export type NoticeEdit = {
  body: string
  tag: string
  /**
   * REQUIRED, and not nullable — rule 9. The editor sends the row's own value
   * back when it has not changed, which is what "leave it alone" looks like in
   * a replace.
   *
   * The server REFUSES a change to this once the notice has started (David,
   * 20 Sep): moving it later un-shows something people have read, keeps every
   * dismissal where it is, and records nothing. The form disables the input in
   * that case rather than letting somebody type a date and be told no.
   */
  startsAt: string
  endsAt: string | null
  dismissible: boolean
  ctaLabel: string | null
  ctaHref: string | null
}

export async function editNotice(
  contentId: string,
  n: NoticeEdit,
): Promise<number> {
  const { data, error } = await supabase.rpc('edit_notice', {
    p_content_id: contentId,
    p_body: n.body,
    p_tag: n.tag || null,
    p_starts_at: n.startsAt,
    p_ends_at: n.endsAt,
    p_dismissible: n.dismissible,
    p_cta_label: n.ctaLabel,
    p_cta_href: n.ctaHref,
  })
  if (error) throw error
  return (data as number) ?? 0
}

export async function dismissNotice(contentId: string): Promise<void> {
  // 176. Viewing as somebody: the write would be refused (read-only), so the
  // close is remembered for this View as session instead of being sent.
  if (viewingAs()) {
    rememberViewAsDismissed(contentId)
    return
  }
  const { error } = await supabase.rpc('dismiss_notice', { p_content_id: contentId })
  if (error) throw error
}

export async function retireNotice(contentId: string): Promise<number> {
  const { data, error } = await supabase.rpc('retire_notice', { p_content_id: contentId })
  if (error) throw error
  return (data as number) ?? 0
}

/* ── the localStorage half, for CODE notices only ──────────────────────────── */

/** id → the version of that notice this viewer dismissed. */
type Dismissals = Record<string, number>

export function readDismissals(): Dismissals {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    // Anything that is not the shape we wrote is treated as nothing dismissed.
    // Showing a notice again is the safe direction; throwing is not.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Dismissals = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

export function recordDismissal(id: string, version: number): Dismissals {
  const next = { ...readDismissals(), [id]: version }
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(next))
  } catch {
    // Storage refused. The notice still disappears for this view, because the
    // caller uses the returned value — it just comes back on reload. A better
    // failure than a dismiss button that does nothing.
  }
  return next
}

/**
 * The code notices this viewer should see, in order.
 *
 * Shown when never dismissed, or when the version has moved past the dismissed
 * one. A non-dismissible notice is always shown, whatever is stored.
 */
export function visibleNotices(all: Notice[], dismissed: Dismissals): Notice[] {
  return all.filter((n) => !n.dismissible || (dismissed[n.id] ?? -1) < n.version)
}

/**
 * 180. Ask publish-post to translate a notice into the languages the company
 * reads that it still lacks — label, words and button wording together. The
 * board sends it after Post, after Save, and on load for anything live or
 * pending. Never throws: a translation that does not arrive leaves readers the
 * original, labelled, which is the fallback, not an error.
 */
export async function translateNotice(contentId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('publish-post', {
      body: { kind: 'translate_notice', content_id: contentId },
    })
    if (error) return []
    return (data as { translated?: string[] } | null)?.translated ?? []
  } catch {
    return []
  }
}

/** One language of one notice, as the board shows it (180). */
export type NoticeLanguage = {
  locale: string
  status: 'original' | 'machine' | 'reviewed' | 'approved' | 'missing'
}

/**
 * Every notice's languages, keyed by content id: the original, each
 * translation and its status, and — for live or pending notices — the
 * languages people read that it does not have yet. content.publish only.
 */
export async function loadNoticeLanguages(): Promise<Map<string, NoticeLanguage[]>> {
  const { data, error } = await supabase.rpc('notice_languages')
  if (error) throw error
  const out = new Map<string, NoticeLanguage[]>()
  for (const r of (data ?? []) as { content_id: string; locale: string; status: NoticeLanguage['status'] }[]) {
    const list = out.get(r.content_id) ?? []
    list.push({ locale: r.locale, status: r.status })
    out.set(r.content_id, list)
  }
  return out
}
